import React, { useState, useEffect } from 'react';

interface HighlightPoint {
  id: string;
  title: string;
  description: string;
  position: {
    top: string;
    left: string;
  };
}

const ResidenceHighlight: React.FC = () => {
  const [activePoint, setActivePoint] = useState<string | null>(null);

  // Fermer la bulle quand on clique n'importe où sur la page (en dehors des points / bulles)
  useEffect(() => {
    const handleDocumentClick = () => {
      if (activePoint !== null) {
        setActivePoint(null);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [activePoint]);

  const highlightPoints: HighlightPoint[] = [
    {
      id: 'security',
      title: 'Accès PMR & sécurité 24h/24',
      description: 'Une résidence sécurisée et un accès adapté pour tous les résidents.',
      position: {
        top: '65%',
        left: '45%',
      },
    },
    {
      id: 'logements',
      title: 'Logements adaptés',
      description:
        'Des logements autonomes et adaptés pour préserver l\'intimité de chacun.',
      position: {
        top: '32%',
        left: '62%',
      },
    },
    {
      id: 'accueil',
      title: 'Un accueil humain quotidien',
      description:
        'Du personnel sur place tous les jours et la téléconciergerie Prudens intégrée au service.',
      position: {
        top: '66%',
        left: '32%',
      },
    },
    {
      id: 'common',
      title: 'Espaces communs conviviaux',
      description:
        'Des lieux de vie partagés pour favoriser les rencontres et profiter des activités offertes.',
      position: {
        top: '64%',
        left: '58%',
      },
    },
    {
      id: 'garden',
      title: 'Jardin paysager sécurisé',
      description:
        'Un espace vert aménagé et sécurisé pour profiter de l\'extérieur en toute sérénité.',
      position: {
        top: '76%',
        left: '88%',
      },
    },
    {
      id: 'repas',
      title: 'Repas partagés',
      description:
        'Offre quotidienne et optionnelle à la disposition des résidents.',
      position: {
        top: '66%',
        left: '72%',
      },
    },
  ];

  return (
    <div className="relative w-full max-w-6xl mx-auto my-12">
      {/* Image de fond (overflow-hidden uniquement sur l'image pour garder les bords arrondis) */}
      <div className="relative w-full aspect-[16/9] rounded-lg shadow-xl overflow-visible">
        <div className="absolute inset-0 rounded-lg overflow-hidden">
          <img
            src="/residence6.png"
            alt="Résidence Prudens"
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Points interactifs (couche au-dessus, overflow visible pour que les bulles ne soient pas coupées) */}
        {highlightPoints.map((point) => {
          const topPercent = parseFloat(point.position.top);
          const showBubbleBelow = topPercent < 50;
          return (
            <div
              key={point.id}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 ${
                activePoint === point.id ? 'z-50' : 'z-20'
              }`}
              style={{
                top: point.position.top,
                left: point.position.left
              }}
            >
              <div
                className="relative cursor-pointer group"
                onClick={(e) => {
                  e.stopPropagation();
                  // Empêche aussi le listener global sur document de se déclencher
                  if ('stopImmediatePropagation' in e.nativeEvent) {
                    (e.nativeEvent as any).stopImmediatePropagation();
                  }
                  setActivePoint(activePoint === point.id ? null : point.id);
                }}
              >
                <div className="w-6 h-6 bg-prudens-bleu-clair rounded-full flex items-center justify-center relative z-10">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
                <div className="absolute inset-0 animate-ping pointer-events-none">
                  <div className="w-6 h-6 bg-prudens-bleu-clair rounded-full opacity-75"></div>
                </div>
                
                {/* Info-bulle : au-dessus ou en dessous selon la position (mobile-friendly) */}
                <div
                  className={`
                    absolute left-1/2 w-64 max-w-[calc(100vw-2rem)] p-4 bg-white rounded-lg shadow-xl z-30
                    transform -translate-x-1/2
                    transition-all duration-300 ease-in-out
                    ${activePoint === point.id ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}
                    border border-gray-100
                  `}
                  style={
                    showBubbleBelow
                      ? { top: '100%', marginTop: '8px' }
                      : { bottom: '100%', marginBottom: '8px' }
                  }
                >
                  <button
                    type="button"
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if ('stopImmediatePropagation' in e.nativeEvent) {
                        (e.nativeEvent as any).stopImmediatePropagation();
                      }
                      setActivePoint(null);
                    }}
                    aria-label="Fermer"
                  >
                    ×
                  </button>
                  <h3 className="font-bold text-prudens-bleu-fonce mb-2 pr-4">{point.title}</h3>
                  <p className="text-sm text-gray-600">{point.description}</p>
                  <div
                    className="absolute left-1/2 w-3 h-3 bg-white border-gray-100 rotate-45"
                    style={
                      showBubbleBelow
                        ? { top: '-6px', transform: 'translateX(-50%) translateY(-50%) rotate(45deg)' }
                        : { bottom: '-6px', transform: 'translateX(-50%) translateY(50%) rotate(45deg)' }
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResidenceHighlight; 